import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  AppBar,
  Toolbar,
  Button,
  Avatar,
  Paper,
  Chip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  School,
  MenuBook,
  ExitToApp,
  PlayCircleOutline,
  TrendingUp,
  Groups,
  EmojiEvents,
  AdminPanelSettings,
  VideoLibrary,
  Assessment,
  Person,
} from "@mui/icons-material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useAuth } from "../contexts/AuthContext";

// Academic Dashboard Theme
const academicTheme = createTheme({
  palette: {
    mode: "light",
    background: {
      default: "#F8F9FA",
      paper: "#FFFFFF",
    },
    primary: {
      main: "#1565C0",
    },
    secondary: {
      main: "#FF8F00",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      color: "#1565C0",
    },
    h6: {
      fontWeight: 600,
    },
  },
});

const DashboardPage = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // ตรวจสอบสิทธิ์ admin
  const username = user?.username || localStorage.getItem("username") || "";
  const isAdmin = username === "smoker8888";

  const menuItems = [
    {
      title: "หลักสูตรการเรียน",
      description: "เข้าเรียนหลักสูตรต่างๆ และวิดีโอการสอน",
      icon: <MenuBook fontSize="large" />,
      color: "#1565C0",
      path: "/courses",
      badge: "12 หลักสูตร",
    },
    {
      title: "ห้องสมุดดิจิตอล",
      description: "คลังความรู้และเอกสารการเรียนรู้",
      icon: <VideoLibrary fontSize="large" />,
      color: "#2E7D32",
      path: "/digital-library",
      badge: "ใหม่",
    },
    {
      title: "ผลการเรียน",
      description: "ตรวจสอบคะแนนและความก้าวหน้า",
      icon: <Assessment fontSize="large" />,
      color: "#FF8F00",
      path: "/learning-results",
      badge: "อัพเดต",
    },
    {
      title: "โปรไฟล์",
      description: "จัดการข้อมูลส่วนตัวและการตั้งค่า",
      icon: <Person fontSize="large" />,
      color: "#7B1FA2",
      path: "/profile",
      badge: "",
    },
    // เมนูแอดมิน - เฉพาะ smoker8888
    ...(isAdmin
      ? [
          {
            title: "จัดการระบบ",
            description: "จัดการบทเรียนและระบบแอดมิน",
            icon: <AdminPanelSettings fontSize="large" />,
            color: "#D32F2F",
            path: "/admin",
            badge: "แอดมิน",
          },
        ]
      : []),
  ];

  const stats = [
    {
      label: "หลักสูตรที่เรียน",
      value: "8",
      icon: <MenuBook />,
      color: "#1565C0",
    },
    {
      label: "วิดีโอที่ดู",
      value: "45",
      icon: <PlayCircleOutline />,
      color: "#2E7D32",
    },
    {
      label: "คะแนนเฉลี่ย",
      value: "87%",
      icon: <TrendingUp />,
      color: "#FF8F00",
    },
    { label: "อันดับ", value: "#23", icon: <EmojiEvents />, color: "#D32F2F" },
  ];

  return (
    <ThemeProvider theme={academicTheme}>
      <Box sx={{ backgroundColor: "#F8F9FA", minHeight: "100vh" }}>
        {/* Header */}
        <AppBar
          position="static"
          sx={{ backgroundColor: "#FFFFFF", boxShadow: 1 }}
        >
          <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexGrow: 1,
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              {!isMobile && (
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    mr: 2,
                    backgroundColor: "#1565C0",
                    flexShrink: 0,
                  }}
                >
                  <School />
                </Avatar>
              )}
              <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                <Typography
                  variant={isMobile ? "body1" : "h6"}
                  sx={{
                    color: "#1565C0",
                    fontWeight: 700,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  ระบบการเรียนรู้ออนไลน์
                </Typography>
                {!isMobile && (
                  <Typography variant="caption" sx={{ color: "#666" }}>
                    Online Learning Management System
                  </Typography>
                )}
              </Box>
            </Box>
            <Button
              startIcon={!isMobile ? <ExitToApp /> : undefined}
              onClick={() => {
                logout?.();
                localStorage.clear();
                navigate("/academic");
              }}
              sx={{
                color: "#D32F2F",
                "&:hover": { backgroundColor: "#FFEBEE" },
                textTransform: "none",
                fontWeight: 600,
                minWidth: "auto",
                px: { xs: 1, sm: 2 },
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
              }}
            >
              {isMobile ? <ExitToApp /> : "ออกจากระบบ"}
            </Button>
          </Toolbar>
        </AppBar>

        {/* Dashboard Content */}
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Welcome Section */}
          <Paper
            sx={{
              background: "linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)",
              color: "white",
              p: 4,
              borderRadius: 3,
              mb: 4,
            }}
          >
            <Typography variant="h4" gutterBottom>
              ยินดีต้อนรับสู่ห้องเรียนออนไลน์
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              เริ่มต้นการเรียนรู้ของคุณวันนี้
            </Typography>
          </Paper>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Card sx={{ textAlign: "center", py: 2 }}>
                  <CardContent>
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        backgroundColor: `${stat.color}15`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 2,
                        color: stat.color,
                      }}
                    >
                      {stat.icon}
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 700, color: stat.color }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Menu Grid */}
          <Typography
            variant="h5"
            sx={{ mb: 3, color: "#1565C0", fontWeight: 600 }}
          >
            เมนูหลัก
          </Typography>
          <Grid container spacing={3}>
            {menuItems.map((item, index) => (
              <Grid item xs={12} sm={6} md={6} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 12px 24px rgba(0,0,0,0.15)",
                    },
                  }}
                >
                  <CardActionArea
                    sx={{ height: "100%", p: 3 }}
                    onClick={() => navigate(item.path)}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 3 }}
                    >
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: 2,
                          backgroundColor: `${item.color}15`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: item.color,
                          flexShrink: 0,
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 600, color: "#1565C0" }}
                          >
                            {item.title}
                          </Typography>
                          {item.badge && (
                            <Chip
                              label={item.badge}
                              size="small"
                              sx={{
                                backgroundColor: `${item.color}15`,
                                color: item.color,
                                fontWeight: 600,
                              }}
                            />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {item.description}
                        </Typography>
                      </Box>
                    </Box>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Quick Access */}
          <Paper sx={{ mt: 4, p: 3, borderRadius: 3 }}>
            <Typography
              variant="h6"
              sx={{ mb: 2, color: "#1565C0", fontWeight: 600 }}
            >
              เข้าถึงด่วน
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Groups />}
                  onClick={() => navigate("/courses")}
                  sx={{
                    py: 1.5,
                    borderColor: "#1565C0",
                    color: "#1565C0",
                    "&:hover": { backgroundColor: "#E3F2FD" },
                  }}
                >
                  หลักสูตรยอดนิยม
                </Button>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<PlayCircleOutline />}
                  onClick={() => navigate("/courses")}
                  sx={{
                    py: 1.5,
                    borderColor: "#2E7D32",
                    color: "#2E7D32",
                    "&:hover": { backgroundColor: "#E8F5E8" },
                  }}
                >
                  วิดีโอใหม่ล่าสุด
                </Button>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Assessment />}
                  onClick={() => navigate("/grades")}
                  sx={{
                    py: 1.5,
                    borderColor: "#FF8F00",
                    color: "#FF8F00",
                    "&:hover": { backgroundColor: "#FFF3E0" },
                  }}
                >
                  ความก้าวหน้า
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default DashboardPage;
